import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/providers/AuthProvider';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import type { TimelineItem, TimelineFilters, TimelineCategory, ClientEventType } from './types';

export const TIMELINE_PAGE_SIZE = 50;

export interface TimelinePage {
  items: TimelineItem[];
  totalCount: number;
}

const MANUAL_EVENT_CATEGORIES: TimelineCategory[] = [
  'contact_phone',
  'contact_email',
  'contact_whatsapp',
  'meeting',
  'milestone',
  'note',
];

interface SourceFlags {
  fetchEvents: boolean;
  fetchDocs: boolean;
  fetchTx: boolean;
  /** Subset dos tipos de client_event a buscar (vazio = todos os manuais). */
  manualEventTypes: ClientEventType[];
}

function decideSources(filters: TimelineFilters): SourceFlags {
  const cats = filters.categories;
  if (cats.length === 0) {
    return { fetchEvents: true, fetchDocs: true, fetchTx: true, manualEventTypes: [] };
  }
  const manualSelected = cats.filter((c) =>
    MANUAL_EVENT_CATEGORIES.includes(c),
  ) as ClientEventType[];
  return {
    fetchEvents: manualSelected.length > 0,
    fetchDocs: cats.includes('document_upload'),
    fetchTx: cats.includes('transaction_created'),
    manualEventTypes: manualSelected,
  };
}

/**
 * Paginação para timeline mesclada de 3 fontes — todos os filtros são aplicados no SQL.
 * Estratégia: para página N (1-indexed), busca (N * pageSize) mais recentes de cada fonte
 * que casam com os filtros, mescla, ordena por data DESC, devolve fatia [start, end].
 * O totalCount também respeita os filtros (counts via head:true, sem trazer linhas).
 */
export function useTimeline(
  page: number = 1,
  filters: TimelineFilters = { categories: [], fromDate: '', toDate: '' },
  pageSize: number = TIMELINE_PAGE_SIZE,
) {
  const { clientId } = useCurrentClient();
  const fetchLimit = page * pageSize;
  const start = (page - 1) * pageSize;
  const end = start + pageSize;

  const fromIso = filters.fromDate ? `${filters.fromDate}T00:00:00.000Z` : null;
  const toIso = filters.toDate ? `${filters.toDate}T23:59:59.999Z` : null;
  const sources = decideSources(filters);

  return useQuery({
    queryKey: ['timeline', clientId, page, pageSize, filters],
    enabled: !!clientId,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<TimelinePage> => {
      if (!clientId) return { items: [], totalCount: 0 };

      // -------- client_events --------
      const eventsQuery = sources.fetchEvents
        ? (() => {
            let q = supabase.from('client_events').select('*').eq('client_id', clientId);
            if (sources.manualEventTypes.length > 0)
              q = q.in('event_type', sources.manualEventTypes);
            if (fromIso) q = q.gte('event_date', fromIso);
            if (toIso) q = q.lte('event_date', toIso);
            return q.order('event_date', { ascending: false }).limit(fetchLimit);
          })()
        : null;

      const eventsCountQuery = sources.fetchEvents
        ? (() => {
            let q = supabase
              .from('client_events')
              .select('id', { count: 'exact', head: true })
              .eq('client_id', clientId);
            if (sources.manualEventTypes.length > 0)
              q = q.in('event_type', sources.manualEventTypes);
            if (fromIso) q = q.gte('event_date', fromIso);
            if (toIso) q = q.lte('event_date', toIso);
            return q;
          })()
        : null;

      // -------- documents --------
      const docsQuery = sources.fetchDocs
        ? (() => {
            let q = supabase
              .from('documents')
              .select('id, name, document_type, supplier_name, created_at')
              .eq('client_id', clientId);
            if (fromIso) q = q.gte('created_at', fromIso);
            if (toIso) q = q.lte('created_at', toIso);
            return q.order('created_at', { ascending: false }).limit(fetchLimit);
          })()
        : null;

      const docsCountQuery = sources.fetchDocs
        ? (() => {
            let q = supabase
              .from('documents')
              .select('id', { count: 'exact', head: true })
              .eq('client_id', clientId);
            if (fromIso) q = q.gte('created_at', fromIso);
            if (toIso) q = q.lte('created_at', toIso);
            return q;
          })()
        : null;

      // -------- transactions --------
      const txQuery = sources.fetchTx
        ? (() => {
            let q = supabase
              .from('transactions')
              .select('id, description, amount, type, created_at')
              .eq('client_id', clientId);
            if (fromIso) q = q.gte('created_at', fromIso);
            if (toIso) q = q.lte('created_at', toIso);
            return q.order('created_at', { ascending: false }).limit(fetchLimit);
          })()
        : null;

      const txCountQuery = sources.fetchTx
        ? (() => {
            let q = supabase
              .from('transactions')
              .select('id', { count: 'exact', head: true })
              .eq('client_id', clientId);
            if (fromIso) q = q.gte('created_at', fromIso);
            if (toIso) q = q.lte('created_at', toIso);
            return q;
          })()
        : null;

      const [eventsRes, docsRes, txRes, eventsCount, docsCount, txCount] = await Promise.all([
        eventsQuery ?? Promise.resolve({ data: [], error: null }),
        docsQuery ?? Promise.resolve({ data: [], error: null }),
        txQuery ?? Promise.resolve({ data: [], error: null }),
        eventsCountQuery ?? Promise.resolve({ count: 0 }),
        docsCountQuery ?? Promise.resolve({ count: 0 }),
        txCountQuery ?? Promise.resolve({ count: 0 }),
      ]);

      if ('error' in eventsRes && eventsRes.error) throw eventsRes.error;
      if ('error' in docsRes && docsRes.error) throw docsRes.error;
      if ('error' in txRes && txRes.error) throw txRes.error;

      const totalCount =
        ('count' in eventsCount ? (eventsCount.count ?? 0) : 0) +
        ('count' in docsCount ? (docsCount.count ?? 0) : 0) +
        ('count' in txCount ? (txCount.count ?? 0) : 0);

      const merged: TimelineItem[] = [];

      for (const ev of (eventsRes.data ?? []) as Array<{
        id: string;
        event_type: ClientEventType;
        title: string;
        description: string | null;
        event_date: string;
        created_by_name: string | null;
      }>) {
        merged.push({
          id: `event:${ev.id}`,
          manualEventId: ev.id,
          category: ev.event_type,
          title: ev.title,
          description: ev.description,
          date: new Date(ev.event_date),
          authorName: ev.created_by_name,
        });
      }

      for (const d of (docsRes.data ?? []) as Array<{
        id: string;
        name: string;
        supplier_name: string | null;
        created_at: string;
      }>) {
        merged.push({
          id: `doc:${d.id}`,
          documentId: d.id,
          category: 'document_upload',
          title: d.name,
          description: d.supplier_name ? `Fornecedor: ${d.supplier_name}` : null,
          date: new Date(d.created_at),
        });
      }

      for (const t of (txRes.data ?? []) as Array<{
        id: string;
        description: string;
        amount: number;
        type: string;
        created_at: string;
      }>) {
        const sign = t.type === 'income' ? '+' : '−';
        const valueStr = Number(t.amount).toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        });
        merged.push({
          id: `tx:${t.id}`,
          transactionId: t.id,
          category: 'transaction_created',
          title: t.description,
          description: `${sign} ${valueStr}`,
          date: new Date(t.created_at),
        });
      }

      merged.sort((a, b) => b.date.getTime() - a.date.getTime());
      return { items: merged.slice(start, end), totalCount };
    },
  });
}

export interface CreateEventInput {
  eventType: ClientEventType;
  title: string;
  description?: string;
  eventDate: string; // ISO timestamptz
}

export function useCreateClientEvent() {
  const qc = useQueryClient();
  const { user, profile } = useAuth();
  const { clientId } = useCurrentClient();

  return useMutation({
    mutationFn: async (input: CreateEventInput) => {
      if (!clientId) throw new Error('Cliente não selecionado');
      if (!user?.id) throw new Error('Não autenticado');

      const { error } = await supabase.from('client_events').insert({
        client_id: clientId,
        event_type: input.eventType,
        title: input.title.trim(),
        description: input.description?.trim() || null,
        event_date: input.eventDate,
        created_by_user_id: user.id,
        created_by_name: profile?.name ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['timeline'] });
      toast.success('Evento adicionado.');
    },
    onError: (e: Error) => toast.error(`Erro ao adicionar: ${e.message}`),
  });
}

export function useDeleteClientEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (eventId: string) => {
      const { error } = await supabase
        .from('client_events')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', eventId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['timeline'] });
      toast.success('Evento removido.');
    },
    onError: (e: Error) => toast.error(`Erro: ${e.message}`),
  });
}
